import { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Button, Empty, TextArea } from "antd-mobile";
import useNavigation from "../../hooks/useNavigation";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError, ToastSuccess } from "../../components/Toast";
import { ImgAttach } from "../../proto/nbchat-proto";
import { CDN_URL } from "../../config/env";
import { staticUrl } from "../../utils";

const MAX_SIZE = 5 * 1024 * 1024;

const Body = styled.div`
  height: calc(100% - ${headerHeight});
  overflow: auto;
  background: #f7f8ff;
`;

const Col = styled.div`
  padding: 10px 15px;
  margin-bottom: 5px;
  background: #fff;
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondaryText || "#999"};
  padding: 5px 0;
`;

const Value = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  line-height: 20px;
  word-break: break-all;
`;

const ImgRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const ImgBox = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
`;

const Thumb = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 6px;
  object-fit: cover;
  background: #f2f2f2;
`;

const Remove = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
`;

const AddBtn = styled.button`
  width: 72px;
  height: 72px;
  border: 1px dashed #ccc;
  border-radius: 6px;
  background: #fafafa;
  color: #999;
  font-size: 28px;
  cursor: pointer;
`;

const MyComplaintForm = () => {
  const { navigate, params } = useNavigation();
  const { orderStore, apiStore } = useStores();
  const message = params.message as any;
  const transNo = message?.attach?.order?.trans_no || params.order_no;
  const [remark, setRemark] = useState("");
  const [images, setImages] = useState<ImgAttach[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const disabled = useMemo(
    () => !remark.trim() || images.length === 0 || loading || uploading,
    [remark, images, loading, uploading]
  );

  const pickImages = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".png,.jpg,.jpeg,image/png,image/jpeg";
    input.multiple = true;
    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (!files.length) return;
      try {
        setUploading(true);
        const uploaded: ImgAttach[] = [];
        for (const file of files) {
          if (!/\.(png|jpe?g)$/i.test(file.name) && !/^image\/(png|jpeg)$/.test(file.type)) {
            ToastError("仅支持 png、jpg 格式");
            continue;
          }
          if (file.size > MAX_SIZE) {
            ToastError("图片需小于 5MB");
            continue;
          }
          const formData = new FormData();
          formData.append("file", file);
          const { uri } = await apiStore.upload({ formData });
          uploaded.push({
            url: uri,
            filename: file.name,
            type: file.type || "image/jpeg",
            size: file.size,
          });
        }
        if (uploaded.length) {
          setImages((prev) => [...prev, ...uploaded]);
        }
      } catch (e: any) {
        ToastError(e?.message || e?.errorMsg || "上传失败");
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const onSubmit = async () => {
    if (!transNo || !message?.message_id) {
      ToastError("缺少订单信息，请从订单消息进入申诉");
      return;
    }
    try {
      setLoading(true);
      orderStore.publicTakeOrderDispute({
        trans_no: transNo,
        last_message_id: message.message_id,
        imgs: images,
        note: remark.trim(),
      } as any);
      ToastSuccess("提交成功");
      navigate("MyComplaint");
    } catch (e: any) {
      ToastError(e.message || "提交失败");
    } finally {
      setLoading(false);
    }
  };

  if (!transNo) {
    return (
      <PageWrap>
        <PageHeader title="申诉订单" />
        <Body>
          <Empty
            description="请从订单消息中点击「异常订单」发起申诉"
            style={{ padding: 40 }}
          />
        </Body>
      </PageWrap>
    );
  }

  return (
    <PageWrap>
      <PageHeader title="申诉订单" />
      <Body>
        <Col>
          <Label>订单号</Label>
          <Value>{transNo}</Value>
        </Col>
        <Col>
          <Label>申诉原因</Label>
          <TextArea
            placeholder="请填写申诉原因"
            maxLength={200}
            rows={4}
            value={remark}
            onChange={setRemark}
            showCount
          />
        </Col>
        <Col>
          <Label>补充截图（png、jpg格式，文件小于5m）</Label>
          <ImgRow>
            {images.map((img, index) => (
              <ImgBox key={`${img.url}-${index}`}>
                <Thumb
                  src={
                    img.url?.startsWith("http")
                      ? img.url
                      : staticUrl(img.url) || `${CDN_URL}/${img.url}`
                  }
                  alt=""
                />
                <Remove
                  type="button"
                  onClick={() =>
                    setImages((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  ×
                </Remove>
              </ImgBox>
            ))}
            <AddBtn type="button" onClick={pickImages} disabled={uploading}>
              {uploading ? "…" : "+"}
            </AddBtn>
          </ImgRow>
        </Col>
        <Col>
          <Button
            color="primary"
            block
            loading={loading}
            disabled={disabled}
            onClick={onSubmit}
          >
            提交
          </Button>
        </Col>
      </Body>
    </PageWrap>
  );
};

export default observer(MyComplaintForm);
